import { useState, useEffect, useRef } from "react";
import { isHeicUrl, convertHeicBlobToJpeg } from "@/lib/utils/heic-converter";

interface HeicImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  mimeType?: string;
}

export function HeicImage({ src, mimeType, className, alt, ...props }: HeicImageProps) {
  const [convertedSrc, setConvertedSrc] = useState<string | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const objectUrlRef = useRef<string | null>(null);
  const lastSrcRef = useRef<string>("");

  const isHeic =
    mimeType?.toLowerCase().includes("heic") ||
    mimeType?.toLowerCase().includes("heif") ||
    isHeicUrl(src || "");

  useEffect(() => {
    if (!src) {
      setStatus("loading");
      setConvertedSrc(null);
      return;
    }

    if (!isHeic) {
      setConvertedSrc(src);
      setStatus("ready");
      return;
    }

    if (lastSrcRef.current === src && objectUrlRef.current) {
      return;
    }

    lastSrcRef.current = src;

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    setStatus("loading");
    setConvertedSrc(null);

    let cancelled = false;

    const convertImage = async () => {
      try {
        const response = await fetch(src);
        if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);

        const blob = await response.blob();
        const jpegBlob = await convertHeicBlobToJpeg(blob);

        if (cancelled) return;

        objectUrlRef.current = URL.createObjectURL(jpegBlob);
        setConvertedSrc(objectUrlRef.current);
        setStatus("ready");
      } catch (err) {
        console.error("Failed to convert HEIC image:", err);
        if (!cancelled) {
          setStatus("error");
        }
      }
    };

    convertImage();

    return () => {
      cancelled = true;
    };
  }, [src, isHeic]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  const placeholderStyle = "min-h-[200px] min-w-[200px] max-w-full";

  if (status === "loading") {
    return (
      <div className={`flex items-center justify-center rounded-lg bg-gray-100 ${placeholderStyle} ${className}`}>
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className={`flex flex-col items-center justify-center gap-2 rounded-lg bg-gray-100 ${placeholderStyle} ${className}`}>
        <span className="text-sm text-gray-500">Something went wrong</span>
        <a
          href={src}
          download
          target="_blank"
          rel="noopener noreferrer"
          className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
        >
          Download Image
        </a>
      </div>
    );
  }

  return (
    <img
      src={convertedSrc || ""}
      alt={alt}
      className={className}
      {...props}
    />
  );
}
